/**
 * Guest Request Flow Review — same-site intake page at /request-review.
 *
 * Streamlined 2026-06-24: the previous version was a 544-line marketing
 * page that duplicated /home (hero, flow visual, slip cards, review-look
 * list, receive list). Now it is a focused intake: brief invitation
 * matching /home's editorial style, then the form.
 *
 * Functional surface preserved exactly:
 *   - POST /api/leads/request-review
 *   - All form fields (name, property_name, email, property_type,
 *     website, main_concern, message)
 *   - Honeypot company_website_hp
 *   - Same data-testids so existing tests still pass
 *   - Same thank-you state
 *
 * Palette mirrors /home: bg #F3F5F7, accent #3F617E.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ['Small hotel', 'Boutique hotel', 'Villa', 'Short-term rental', 'Other'];
const MAIN_CONCERNS = [
  'Guest requests get missed',
  'Follow-ups are delayed',
  'Reviews mention service issues',
  'Shift handover is messy',
  'Requests come from too many places',
  'Not sure yet',
];
// Multi-select — mirrors the /audit form. Payload key `intake_channels`
// is stored on the lead document and appears in the admin notification
// email alongside the other lead fields.
const INTAKE_CHANNELS = [
  'WhatsApp',
  'Email',
  'Phone',
  'Front desk notes',
  'Guest app',
  'Booking platform messages',
  'Walk-ins / verbal requests',
  'Other',
];

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center text-[10.5px] tracking-[0.22em] uppercase font-medium text-[#3F617E]">
    <span>{children}</span>
  </div>
);

const RequestReview = () => {
  const [form, setForm] = useState({
    name: '', property_name: '', email: '', property_type: '',
    website: '', main_concern: '', message: '', company_website_hp: '',
    intake_channels: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target?.value ?? e }));

  // Toggle a channel in/out of the multi-select `intake_channels` array.
  const toggleChannel = (channel) => (checked) => {
    setForm((prev) => {
      const set = new Set(prev.intake_channels);
      if (checked) set.add(channel);
      else set.delete(channel);
      return { ...prev, intake_channels: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!form.name.trim() || !form.property_name.trim() || !form.email.trim()) {
      setError('Please share your name, property, and email so we can follow up.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/request-review`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Thank-you state ---------- */
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center px-6 py-16" data-testid="lead-confirmation">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex h-12 w-12 rounded-full bg-[#3F617E]/10 items-center justify-center mb-5">
            <CheckCircle2 className="h-6 w-6 text-[#3F617E]" />
          </div>
          <Eyebrow>Received</Eyebrow>
          <h1 className="font-heading text-[26px] sm:text-[30px] text-stone-900 mt-4">Thank you.</h1>
          <p className="text-stone-600 leading-relaxed text-[15px] mt-3">
            Your request has been received. We&apos;ll review your property details and follow up shortly.
          </p>
          <p className="text-[12px] tracking-[0.18em] uppercase text-stone-500 mt-7">
            — The Maris Desk team
          </p>
          <Link to="/home" className="inline-flex items-center gap-2 text-[13px] text-stone-500 hover:text-stone-900 mt-8 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Maris Desk
          </Link>
        </div>
      </div>
    );
  }

  /* ---------- Intake page ---------- */
  return (
    <div className="min-h-screen bg-[#F3F5F7] text-stone-900 antialiased" data-testid="request-review-page">

      {/* ---------------------- Top nav ---------------------- */}
      <header className="border-b border-stone-200/70 bg-[#F3F5F7]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2 group" data-testid="rr-nav-logo">
            <span className="font-heading text-[17px] sm:text-[19px] text-stone-900 group-hover:text-[#3F617E] transition-colors">
              Maris Desk
            </span>
            <span className="hidden sm:inline-flex text-[10px] tracking-[0.18em] uppercase text-stone-400 border-l border-stone-200 pl-2.5">
              Guest Request Flow Review
            </span>
          </Link>
          <Link
            to="/home"
            className="inline-flex items-center gap-1.5 text-[11.5px] sm:text-xs text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="rr-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        </div>
      </header>

      {/* ---------------------- Intake ---------------------- */}
      <section className="pt-10 sm:pt-14 md:pt-16 pb-14 md:pb-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-6">
          <Eyebrow>Request your review</Eyebrow>
          <h1 className="font-heading text-[28px] sm:text-[34px] md:text-[40px] leading-[1.08] tracking-tight text-stone-900 mt-5">
            Start with a Guest Request Flow Review
          </h1>
          <p className="text-stone-600 leading-relaxed text-[15px] sm:text-[16px] mt-5">
            Share a few details about your property and we&apos;ll be in touch with next steps. The review
            looks at where guest requests arrive, who owns them, how follow-ups happen, and what carries
            into the next shift.
          </p>
          <p className="mt-5 text-[11.5px] tracking-[0.14em] uppercase text-[#3F617E] font-semibold">
            Channels · Ownership · Follow-up · Handover
          </p>

          {/* Form — same fields, same testids, same submit logic as before. */}
          <form className="mt-10 space-y-5" onSubmit={handleSubmit}>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">
                  Name <span className="text-[#3F617E]">*</span>
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Your full name"
                  className="bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                  data-testid="lead-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="property_name" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">
                  Property / hotel name <span className="text-[#3F617E]">*</span>
                </Label>
                <Input
                  id="property_name"
                  value={form.property_name}
                  onChange={update('property_name')}
                  placeholder="e.g. SeaGlass Villa"
                  className="bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                  data-testid="lead-property-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">
                Email <span className="text-[#3F617E]">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder="you@example.com"
                className="bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                data-testid="lead-email-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] tracking-[0.08em] uppercase text-stone-500">Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger className="bg-white border-stone-300 focus:ring-[#3F617E]/40" data-testid="lead-property-type">
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="website" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">
                  Website or booking page
                </Label>
                <Input
                  id="website"
                  value={form.website}
                  onChange={update('website')}
                  placeholder="https://..."
                  className="bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                  data-testid="lead-website-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] tracking-[0.08em] uppercase text-stone-500">
                Where do guest requests usually come in today?
              </Label>
              <p className="text-[12px] text-stone-500 leading-relaxed" data-testid="lead-intake-channels-help">
                Select all that apply.
              </p>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 pt-2"
                role="group"
                aria-label="Where do guest requests usually come in today?"
                data-testid="lead-intake-channels"
              >
                {INTAKE_CHANNELS.map((channel) => {
                  const checked = form.intake_channels.includes(channel);
                  const slug = channel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                  const inputId = `intake-channel-${slug}`;
                  return (
                    <label
                      key={channel}
                      htmlFor={inputId}
                      className="flex items-start gap-2.5 cursor-pointer group"
                    >
                      <Checkbox
                        id={inputId}
                        checked={checked}
                        onCheckedChange={toggleChannel(channel)}
                        className="mt-0.5 border-stone-400 data-[state=checked]:bg-[#3F617E] data-[state=checked]:border-[#3F617E] data-[state=checked]:text-white focus-visible:ring-[#3F617E]/40"
                        data-testid={`lead-intake-channel-${slug}`}
                      />
                      <span className="text-[14px] text-stone-800 leading-tight select-none group-hover:text-stone-900">
                        {channel}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] tracking-[0.08em] uppercase text-stone-500">Main concern</Label>
              <Select value={form.main_concern} onValueChange={(v) => setForm({ ...form, main_concern: v })}>
                <SelectTrigger className="bg-white border-stone-300 focus:ring-[#3F617E]/40" data-testid="lead-main-concern">
                  <SelectValue placeholder="What feels heaviest right now?" />
                </SelectTrigger>
                <SelectContent>
                  {MAIN_CONCERNS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="message" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">
                Message / notes
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={update('message')}
                placeholder="Anything else you'd like us to know?"
                className="min-h-[110px] bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                data-testid="lead-message-input"
              />
            </div>

            {error && (
              <div className="text-[13.5px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2.5">
                {error}
              </div>
            )}

            <div className="pt-3">
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full h-12 text-[15px] font-medium tracking-wide shadow-md shadow-[#3F617E]/15"
                data-testid="lead-submit-button"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Request My Review
              </Button>
              <p className="text-[11.5px] text-stone-500 text-center mt-3 leading-relaxed max-w-md mx-auto">
                By submitting this form, you agree that Maris Desk may contact you about your Guest
                Request Flow Review.
              </p>
            </div>

            {/* Honeypot — bots auto-fill, humans don't.
                Non-empty value is silently dropped server-side. */}
            <input
              type="text"
              name="company_website_hp"
              tabIndex={-1}
              autoComplete="off"
              value={form.company_website_hp}
              onChange={update('company_website_hp')}
              style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
              aria-hidden="true"
              data-testid="lead-honeypot-input"
            />
          </form>
        </div>
      </section>

      {/* ---------------------- Footer ---------------------- */}
      <footer className="border-t border-stone-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 text-[12.5px] text-stone-500">
          <div className="flex items-center gap-3">
            <span className="font-heading text-base text-stone-900">Maris Desk</span>
            <span className="hidden sm:inline-flex text-[10.5px] tracking-[0.2em] uppercase text-stone-400">
              Calm hospitality operations
            </span>
          </div>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Footer">
            <Link to="/home" className="hover:text-stone-900 transition-colors">Maris Desk</Link>
            <a href="/privacy" className="hover:text-stone-900 transition-colors" data-testid="footer-privacy-link">Privacy</a>
            <a href="/terms" className="hover:text-stone-900 transition-colors" data-testid="footer-terms-link">Terms</a>
            <a href="mailto:support@marisdesk.com" className="hover:text-stone-900 transition-colors" data-testid="footer-contact-link">
              support@marisdesk.com
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default RequestReview;
